/**
 * Load out my van — the driver-cum-salesman's own load-out.
 *
 * EXACTLY the admin Stock → Load out tab (owner, 1 Nov: "I want this feature to
 * be exactly same as how it has been given to the admin"), reproducing its every
 * column — In godown, In van, Order Received, Additional Pets (pets + loose
 * pieces), Total load — its per-category subtotal rows, its instruction line, its
 * "Total load = in van + order + additional" arithmetic, and its red load-summary
 * for counting boxes off. The ONE difference is the Trip card: the admin picks the
 * vehicle, driver, godown and beat from dropdowns, whereas here they are FIXED by
 * the day the field user already started (his van, himself, his beat), so they are
 * shown read-only and only the trip number is editable.
 *
 * "Optimise the display accordingly": the admin's single wide six-column table
 * does not fit a phone, so the SAME figures are laid out as a stacked, fit-to-
 * width list — a shaded category header carrying that category's five subtotals,
 * then each product as a compact card with its three read-only figures, the two
 * inputs, and its Total load. No horizontal scrolling, nothing dropped.
 *
 * Deliberately FIELD-accessible: it reads the field user's own day and the beat's
 * requirement (both field endpoints: /api/field/day/current and the shared,
 * authenticated /api/stock/load-requirements), never the admin masters the admin
 * screen uses, which a field user cannot open. The server (POST
 * /api/stock/load-sheets) enforces that the vehicle is his own open-session van
 * and records it as accepted by the act of loading.
 */

import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Page } from '../../components/Shell';
import { Banner, Button, Card, Chip, Skeleton, TextInput, useToast } from '../../components/ui';
import { useQuery } from '../../lib/useApi';
import { api } from '../../lib/api';

interface DayCurrent {
  started: boolean;
  closed?: boolean;
  canLoadOwnVan?: boolean;
  van?: { vehicleId: string; registration: string | null } | null;
  driverId?: string | null;
  defaultGodownId?: string | null;
  beat?: { beatId: string; beatName: string; beatCode: string } | null;
}

interface ReqItem {
  productId: string; skuCode: string; productName: string; uom: string;
  category: string; packQty: number;
  godownPets: number; vanPets: number; requiredPets: number; minToAddPets: number;
}
interface ReqCat {
  category: string; godownPets: number; vanPets: number; requiredPets: number; minToAddPets: number;
}
interface ReqResp { items: ReqItem[]; categories: ReqCat[] }

/** One read-only figure in a product card — matches a column of the admin table. */
function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ flex: 1, background: 'var(--line-2, #eef0f3)', borderRadius: 8, padding: '6px 4px', textAlign: 'center' }}>
      <div className="t-xs t-muted" style={{ fontSize: 10.5 }}>{label}</div>
      <div className="t-sm t-semi">{value}</div>
    </div>
  );
}

export default function LoadOutMyVan() {
  const navigate = useNavigate();
  const toast = useToast();

  const day = useQuery<DayCurrent>('/api/field/day/current');
  // Additional PETS on top of the order requirement, per SKU — the admin's
  // "Additional Pets" column. Loose PIECES go in alongside, for a partial box.
  const [extra, setExtra] = useState<Record<string, string>>({});
  const [extraPieces, setExtraPieces] = useState<Record<string, string>>({});
  // A second trip on the same day is trip 2 — the admin screen's trip number.
  const [tripNo, setTripNo] = useState('1');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const van = day.data?.van ?? null;
  const beat = day.data?.beat ?? null;
  const ready = !!(day.data?.canLoadOwnVan && van && beat && day.data?.driverId && day.data?.defaultGodownId);

  const req = useQuery<ReqResp>(
    ready ? `/api/stock/load-requirements?beatId=${beat!.beatId}&vehicleId=${van!.vehicleId}` : null,
    { deps: [beat?.beatId, van?.vehicleId] },
  );

  // Derived rows, identical arithmetic to the admin screen: load = required +
  // additional (pets), base pieces = pets × pack + loose, and it must fit the
  // godown. "Total load" (the display) is in-van + order + additional, i.e. what
  // the van will hold after this load; the quantity SUBMITTED is only what is
  // newly loaded (required + additional), exactly as the admin submits.
  const rows = (req.data?.items ?? []).map((it) => {
    const additional = Number(extra[it.productId]) || 0;
    const loosePieces = Number(extraPieces[it.productId]) || 0;
    const loadPets = it.requiredPets + additional;
    const loadPieces = loadPets * it.packQty + loosePieces;
    const godownPieces = it.godownPets * it.packQty;
    return { ...it, additional, loosePieces, loadPets, loadPieces, godownPieces };
  });
  const chosen = rows.filter((r) => r.loadPieces > 0);
  const overloaded = chosen.filter((r) => r.loadPieces > r.godownPieces);
  const totalPets = chosen.reduce((s, r) => s + r.loadPets, 0);
  const totalLoosePieces = chosen.reduce((s, r) => s + r.loosePieces, 0);
  const canSubmit = ready && chosen.length > 0 && overloaded.length === 0 && !saving;

  async function submit() {
    if (!ready) return;
    setSaving(true);
    setErrors({});
    try {
      const res = await api.post<{ loadSheetNumber: string; skuCount: number; stopCount: number }>(
        '/api/stock/load-sheets',
        {
          vehicleId: van!.vehicleId,
          driverId: day.data!.driverId,
          godownId: day.data!.defaultGodownId,
          beatId: beat!.beatId,
          tripNo: Number(tripNo) || 1,
          // Base pieces = whole pets × pack + loose pieces, the unit the ledger keeps.
          lines: chosen.map((r) => ({ productId: r.productId, quantity: r.loadPieces })),
        },
      );
      toast.show(`${res.loadSheetNumber} loaded · ${res.skuCount} SKU(s)`, 'ok');
      setExtra({});
      setExtraPieces({});
      req.refetch();
      navigate('/day');
    } catch (e) {
      const err = e as { fields?: Record<string, string>; message?: string };
      if (err.fields) setErrors(err.fields);
      else toast.show(err.message ?? 'Could not load the van', 'danger');
    } finally {
      setSaving(false);
    }
  }

  if (day.loading && !day.data) return <Page title="Load out my van" back><Skeleton rows={4} /></Page>;

  if (!day.data?.started) {
    return (
      <Page title="Load out my van" back>
        <Banner tone="warn" title="Start your day first">
          <span className="t-sm">Open your day and choose your van, then come back to load it.</span>
        </Banner>
        <Button block onClick={() => navigate('/day')}>Go to My day</Button>
      </Page>
    );
  }

  if (!ready) {
    return (
      <Page title="Load out my van" back>
        <Banner tone="warn" title="No van to load">
          <span className="t-sm">
            Loading your own van is for a driver‑cum‑salesman who has started the day on a vehicle.
            If a manager or admin loads for you, accept the load from your Deliveries screen instead.
          </span>
        </Banner>
      </Page>
    );
  }

  return (
    <Page title="Load out my van" back>
      <div className="stack">
        <Banner tone="info" title="This is where warehouse stock becomes van stock">
          <span className="t-sm">
            You become accountable for everything you load. Quantities are checked against Business
            Central less what is already on a van, so a load cannot exceed what the warehouse holds.
          </span>
        </Banner>

        {/* Trip — the admin's five dropdowns, but fixed by the day you started.
            Only the trip number is yours to set (a second trip today is trip 2). */}
        <Card className="stack-2">
          <div className="t-sm t-semi">Trip</div>
          <div className="stack-1" style={{ padding: '2px 0' }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="t-xs t-muted">Vehicle</span>
              <span className="t-sm t-semi">{van!.registration ?? van!.vehicleId}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="t-xs t-muted">Driver</span>
              <span className="t-sm t-semi">You</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="t-xs t-muted">Beat</span>
              <span className="t-sm t-semi">{beat!.beatName}</span>
            </div>
          </div>
          <TextInput
            label="Trip number" numeric value={tripNo}
            onChange={(e) => setTripNo(e.target.value.replace(/\D/g, '').slice(0, 2))}
            hint="A second trip on the same day is trip 2"
          />
        </Card>

        {errors.lines && <Banner tone="danger" title="Cannot load">{errors.lines}</Banner>}

        {/* Requirement for this beat — the admin table, stacked for a phone. */}
        <Card className="stack-2">
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="t-sm t-semi grow">Requirement for this beat</span>
            {(totalPets > 0 || totalLoosePieces > 0) && (
              <Chip tone="ok">{totalPets} pets{totalLoosePieces > 0 ? ` · ${totalLoosePieces} pcs` : ''} to load</Chip>
            )}
          </div>
          <div className="t-xs t-muted">
            Enter whole pets (boxes) and, if a partial box goes on the van, loose pieces. A pet is a box of pieces.
          </div>

          {req.loading && !req.data && <Skeleton rows={3} />}
          {req.data && req.data.items.length === 0 && (
            <div className="t-sm t-muted" style={{ padding: 12 }}>No pending orders routed to this beat.</div>
          )}

          {req.data && req.data.items.length > 0 && (
            <div className="stack-2">
              {(req.data.categories ?? []).map((cat) => {
                const catRows = rows.filter((r) => r.category === cat.category);
                const catAdd = catRows.reduce((s, r) => s + r.additional, 0);
                // Total load subtotal — in van + order + additional, like the admin.
                const catTotal = cat.vanPets + cat.requiredPets + catAdd;
                return (
                  <Fragment key={cat.category}>
                    {/* Category header — carries the five subtotals of the admin's
                        shaded category row, wrapped to fit a narrow screen. */}
                    <div style={{ background: 'var(--line-2, #eef0f3)', borderRadius: 8, padding: '8px 10px' }}>
                      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                        <span className="t-sm t-semi">{cat.category}</span>
                        <span className="t-sm t-semi">Total {catTotal}</span>
                      </div>
                      <div className="t-xs t-muted" style={{ marginTop: 2 }}>
                        In godown {cat.godownPets} · In van {cat.vanPets} · Order {cat.requiredPets} · Added {catAdd}
                      </div>
                    </div>

                    {catRows.map((r) => {
                      const over = r.loadPieces > r.godownPieces;
                      const totalLoad = r.vanPets + r.requiredPets + r.additional;
                      return (
                        <div
                          key={r.productId}
                          style={{
                            border: `1px solid var(${over ? '--danger' : '--line'})`,
                            borderRadius: 10, padding: '10px 11px',
                          }}
                        >
                          <div className="t-sm t-semi">{r.productName}</div>
                          <div className="t-xs t-muted" style={{ marginBottom: 8 }}>{r.skuCode} · {r.packQty}/pet</div>

                          <div className="row" style={{ gap: 6, marginBottom: 9 }}>
                            <Stat label="In godown" value={r.godownPets} />
                            <Stat label="In van" value={r.vanPets} />
                            <Stat label="Order" value={r.requiredPets} />
                          </div>

                          <div className="row" style={{ gap: 8, alignItems: 'flex-end' }}>
                            <label style={{ flex: 1 }}>
                              <span className="t-xs t-muted" style={{ display: 'block', marginBottom: 3 }}>Additional pets</span>
                              <TextInput
                                numeric value={extra[r.productId] ?? ''} placeholder="0"
                                aria-label={`Pets to add of ${r.productName}`}
                                error={over ? 'Too many' : undefined}
                                onChange={(e) => setExtra((p) => ({ ...p, [r.productId]: e.target.value.replace(/\D/g, '') }))}
                              />
                            </label>
                            <label style={{ flex: 1 }}>
                              <span className="t-xs t-muted" style={{ display: 'block', marginBottom: 3 }}>Loose pieces</span>
                              <TextInput
                                numeric value={extraPieces[r.productId] ?? ''} placeholder="0"
                                aria-label={`Loose pieces to add of ${r.productName}`}
                                onChange={(e) => setExtraPieces((p) => ({ ...p, [r.productId]: e.target.value.replace(/\D/g, '') }))}
                              />
                            </label>
                          </div>

                          <div className="row" style={{ justifyContent: 'space-between', marginTop: 9 }}>
                            <span className="t-xs t-muted">Total load</span>
                            <span className={`t-sm t-semi${over ? ' t-danger' : ''}`}>
                              {totalLoad} pets{r.loosePieces > 0 ? ` ${r.loosePieces} pcs` : ''}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </Fragment>
                );
              })}
            </div>
          )}
        </Card>

        {/* Load summary — total pets on the van, category-wise, in bold red so you
            can read it off and count the boxes quickly. Same as the admin. */}
        {totalPets > 0 && (
          <Card className="stack-2">
            <div className="t-sm t-semi">Load summary — total pets on the van (for you to count)</div>
            {(req.data?.categories ?? []).map((cat) => {
              const catItems = rows
                .filter((r) => r.category === cat.category)
                .map((r) => ({ r, total: r.vanPets + r.requiredPets + r.additional }))
                .filter((x) => x.total > 0);
              if (catItems.length === 0) return null;
              const catTotal = catItems.reduce((s, x) => s + x.total, 0);
              return (
                <div key={cat.category} style={{ borderTop: '1px solid var(--line)', paddingTop: 6 }}>
                  <div style={{ color: 'var(--danger, #c0392b)', fontWeight: 800, fontSize: 15 }}>
                    {cat.category} — {catTotal} pets
                  </div>
                  {catItems.map(({ r, total }) => (
                    <div key={r.productId} style={{ color: 'var(--danger, #c0392b)', fontWeight: 700, paddingLeft: 14 }}>
                      {r.productName} — {total} pets
                    </div>
                  ))}
                </div>
              );
            })}
          </Card>
        )}

        {overloaded.length > 0 && (
          <Banner tone="danger" title="More than the warehouse holds">
            <span className="t-sm">
              {overloaded.map((o) => o.skuCode).join(', ')} — reduce the quantity to what the godown has.
            </span>
          </Banner>
        )}

        <Button block busy={saving} disabled={!canSubmit} onClick={() => void submit()}>
          {saving ? 'Loading out…' : `Load the van${totalPets > 0 ? ` · ${totalPets} pets${totalLoosePieces > 0 ? ` ${totalLoosePieces} pcs` : ''}` : ''}`}
        </Button>
        <p className="t-xs t-muted t-center">
          You loaded it, so your acceptance is automatic. Deliveries unlock once an admin or manager
          approves your beat &amp; load.
        </p>
      </div>
    </Page>
  );
}
